/**
 * ErrorBoundary.js
 * Purpose: Catch render errors across the app, log details with a correlation ID,
 * and show a safe fallback UI so users aren't blocked by crashes.
 *
 * Notes:
 * - No external logger dependency to keep root app lightweight.
 * - Correlation ID helps trace errors in logs/support tickets.
 */

import React from 'react';

import logger, { genCorrelationId } from '../utils/logger';
import { trackError } from '../utils/errorTracking';

// Correlation IDs are now provided by the centralized logger util

// Default fallback UI (kept framework-agnostic; Chakra is optional here)
// Provides clear recovery options for users when the app crashes
// NOTE: We intentionally use raw hex colors here because the theme may be unavailable
// when the ErrorBoundary catches a crash. This ensures the fallback always renders correctly.
/* eslint-disable no-restricted-syntax */
/**
 * @param {{ error: unknown, correlationId?: string }} props
 */
function DefaultFallback({ error, correlationId }) {
  /**
   * Safely reload the page, clearing any corrupted state
   */
  const handleReload = () => {
    try {
      // Clear any potentially corrupted session storage
      sessionStorage.removeItem('timegenie_auto_recovery_attempted');
    } catch (_) {
      // Storage access may fail in private browsing
    }
    window.location.reload();
  };

  /**
   * Clear all app cache and reload (nuclear option)
   */
  const handleClearAndReload = () => {
    try {
      // Clear localStorage keys related to the app
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('timegenie') || key.includes('cache'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch (_) {
      // Storage access may fail
    }
    window.location.reload();
  };

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 480,
        margin: '60px auto',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <h2 style={{ marginBottom: 12, color: '#1a202c' }}>Something went wrong</h2>
      <p style={{ marginBottom: 20, color: '#4a5568' }}>
        We hit an unexpected error. This is usually temporary.
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 20 }}>
        <button
          onClick={handleReload}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3182ce',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Reload Page
        </button>
        <button
          onClick={handleClearAndReload}
          style={{
            padding: '10px 20px',
            backgroundColor: '#e2e8f0',
            color: '#2d3748',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Clear Cache & Reload
        </button>
      </div>
      {process.env.NODE_ENV !== 'production' && (
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
            padding: 12,
            backgroundColor: '#fff5f5',
            borderRadius: 6,
            fontSize: 12,
            color: '#c53030',
            overflow: 'auto',
          }}
        >
          {String(/** @type {any} */ (error)?.message || error)}
          {correlationId ? `\n\nCorrelation ID: ${correlationId}` : ''}
        </pre>
      )}
      {process.env.NODE_ENV === 'production' && correlationId && (
        <p style={{ fontSize: 12, color: '#a0aec0' }}>Error ID: {correlationId}</p>
      )}
    </div>
  );
}
/* eslint-enable no-restricted-syntax */

/**
 * ErrorBoundary class component to catch render errors.
 * - getDerivedStateFromError updates UI state when an error is caught
 * - componentDidCatch logs the error with a generated correlation id
 *
 * @typedef {{
 *   children?: React.ReactNode,
 *   fallback?: React.ComponentType<{ error: unknown, correlationId?: string }>
 * }} ErrorBoundaryProps
 */
export default class ErrorBoundary extends React.Component {
  /** @param {any} props */
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, correlationId: null };
  }

  /** @param {any} error */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /** @param {any} error @param {any} info */
  componentDidCatch(error, info) {
    const cid = genCorrelationId();
    this.setState({ correlationId: cid });
    // Use centralized logger with scoped correlation id for traceability
    const log = logger.createScope({ context: 'ErrorBoundary', cid });
    log.error('React ErrorBoundary caught error', { error, info });

    // Track error to Supabase for crash analytics
    trackError(error, {
      source: 'ErrorBoundary',
      correlation_id: cid,
      component_stack: info?.componentStack || null,
    });
  }

  render() {
    const { hasError, error, correlationId } = this.state;
    /** @type {React.ComponentType<{ error: unknown, correlationId?: string }>} */
    const Fallback = (this.props && this.props.fallback) || DefaultFallback;

    if (hasError) {
      return <Fallback error={error} correlationId={correlationId || undefined} />;
    }

    return this.props.children;
  }
}
